import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, XCircle, Clock, FlaskConical, User, Stethoscope, Calendar, Hash } from 'lucide-react';
import api from '@/lib/axios';

interface PublicSolicitud {
  folio: string;
  fecha: string;
  status: string;
  indicaciones: string | null;
  codigoCie11: string | null;
  tituloCie11: string | null;
  estudios: { nombre: string; descripcion?: string }[];
  paciente: { nombre: string };
  medico: { nombre: string; cedula: string | null; especialidad: string | null } | null;
}

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
}

export function EstudioDigitalPage() {
  const { folio } = useParams<{ folio: string }>();

  const { data, isLoading, isError } = useQuery<PublicSolicitud>({
    queryKey: ['estudio-publico', folio],
    queryFn: () => api.get(`/api/public/estudios/${folio}`).then(r => r.data),
    retry: false,
  });

  const isActiva    = data?.status === 'activa';
  const isCancelada = data?.status === 'cancelada';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center">
            <FlaskConical size={16} className="text-white" />
          </div>
          <div>
            <p className="text-xs text-gray-400 leading-none">Verificación de</p>
            <p className="text-sm font-semibold text-gray-800 leading-tight">Solicitud de Laboratorio</p>
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8">
        {isLoading && (
          <div className="text-center py-20">
            <div className="w-10 h-10 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-500">Verificando solicitud...</p>
          </div>
        )}

        {isError && (
          <div className="text-center py-20">
            <XCircle size={48} className="text-red-400 mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-700 mb-1">Solicitud no encontrada</p>
            <p className="text-sm text-gray-500">El folio <strong>{folio}</strong> no corresponde a ninguna solicitud registrada.</p>
          </div>
        )}

        {data && (
          <div className="space-y-4">
            {/* Status badge */}
            <div className={`rounded-2xl p-5 flex items-center gap-4 ${
              isActiva
                ? 'bg-green-50 border border-green-200'
                : isCancelada
                  ? 'bg-gray-50 border border-gray-200'
                  : 'bg-amber-50 border border-amber-200'
            }`}>
              {isActiva ? (
                <CheckCircle2 size={40} className="text-green-500 flex-shrink-0" />
              ) : isCancelada ? (
                <XCircle size={40} className="text-gray-400 flex-shrink-0" />
              ) : (
                <Clock size={40} className="text-amber-500 flex-shrink-0" />
              )}
              <div>
                <p className={`text-xl font-bold ${
                  isActiva ? 'text-green-700' : isCancelada ? 'text-gray-500' : 'text-amber-700'
                }`}>
                  {isActiva ? 'Solicitud Válida' : isCancelada ? 'Solicitud Cancelada' : 'Solicitud en Borrador'}
                </p>
                <p className="text-sm text-gray-500 mt-0.5">
                  {isActiva
                    ? 'Esta solicitud es válida y fue emitida por un médico autorizado.'
                    : isCancelada
                      ? 'Esta solicitud fue cancelada y no tiene validez.'
                      : 'Esta solicitud aún no ha sido emitida formalmente.'}
                </p>
              </div>
            </div>

            {/* Folio + fecha */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4 grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Hash size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Folio</p>
                  <p className="text-sm font-semibold text-gray-800 font-mono">{data.folio}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Fecha de emisión</p>
                  <p className="text-sm font-medium text-gray-700">{formatDate(data.fecha)}</p>
                </div>
              </div>
            </div>

            {/* Médico */}
            {data.medico && (
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Stethoscope size={15} className="text-teal-500" />
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Médico</p>
                </div>
                <p className="text-base font-semibold text-gray-800">{data.medico.nombre}</p>
                <div className="flex flex-wrap gap-3 mt-1">
                  {data.medico.cedula && (
                    <p className="text-xs text-gray-500">Cédula: <span className="font-medium text-gray-700">{data.medico.cedula}</span></p>
                  )}
                  {data.medico.especialidad && (
                    <p className="text-xs text-gray-500">{data.medico.especialidad}</p>
                  )}
                </div>
              </div>
            )}

            {/* Paciente */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <User size={15} className="text-teal-500" />
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Paciente</p>
              </div>
              <p className="text-base font-semibold text-gray-800">{data.paciente.nombre}</p>
              <p className="text-xs text-gray-400 mt-0.5">Nombre parcialmente oculto por privacidad</p>
            </div>

            {/* Diagnóstico */}
            {data.codigoCie11 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Diagnóstico</p>
                <p className="text-sm text-gray-700">
                  <span className="font-mono text-[11px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded mr-2">{data.codigoCie11}</span>
                  {data.tituloCie11}
                </p>
              </div>
            )}

            {/* Indicaciones */}
            {data.indicaciones && (
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Indicaciones</p>
                <p className="text-sm text-gray-700">{data.indicaciones}</p>
              </div>
            )}

            {/* Estudios */}
            {data.estudios?.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <FlaskConical size={15} className="text-teal-500" />
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Estudios solicitados ({data.estudios.length})
                  </p>
                </div>
                <div className="space-y-3">
                  {data.estudios.map((e, i) => (
                    <div key={i} className="flex gap-3 pb-3 border-b border-gray-50 last:border-0 last:pb-0">
                      <span className="w-6 h-6 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{e.nombre}</p>
                        {e.descripcion && <p className="text-xs text-gray-400 mt-0.5">{e.descripcion}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <p className="text-center text-xs text-gray-400 py-4">
              Documento generado digitalmente · Sistema de Gestión Clínica
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
